import React, {useContext, useEffect, useState} from 'react';
import type {NextPage} from 'next';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {InputText} from 'primereact/inputtext';
import {Password} from 'primereact/password';
import {Button} from 'primereact/button';
import {classNames} from 'primereact/utils';
import {AuthContext} from '../../../.contexts/AuthContext';
import {ToastContext} from '../../../.contexts/ToastContext';
import FieldMessage from '../../../components/FieldMessage/FieldMessage';
import {requestOtp} from '../../../services/authService';

const passwordSchema = yup.object({email: yup.string().email().required(), password: yup.string().required()});
const emailSchema = yup.object({email: yup.string().email().required()});
const codeSchema = yup.object({code: yup.string().matches(/^\d{6}$/, 'Informe os seis dígitos do código.').required()});
type Challenge = {id: string; email: string; message: string; expiresAt: number; resendAt: number};

function maskEmail(email: string) {
  const [name, domain = ''] = email.split('@');
  const visible = name.slice(0, Math.min(2, name.length));
  return `${visible}${'*'.repeat(Math.max(2, name.length - visible.length))}@${domain}`;
}

const LoginAsManager: NextPage = () => {
  const {signIn, signInWithOtp} = useContext(AuthContext);
  const {showToast} = useContext(ToastContext);
  const [mode, setMode] = useState<'password' | 'email' | 'code'>('password');
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(Date.now());
  const passwordForm = useForm({defaultValues: {email: '', password: ''}, resolver: yupResolver(passwordSchema)});
  const emailForm = useForm({defaultValues: {email: ''}, resolver: yupResolver(emailSchema)});
  const codeForm = useForm({defaultValues: {code: ''}, resolver: yupResolver(codeSchema)});

  useEffect(() => {
    if (mode !== 'code') return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [mode]);

  const expiresIn = challenge ? Math.max(0, Math.ceil((challenge.expiresAt - now) / 1000)) : 0;
  const resendIn = challenge ? Math.max(0, Math.ceil((challenge.resendAt - now) / 1000)) : 0;
  const errorToast = (detail: string) => showToast({severity: 'error', summary: 'Erro ao processar esta requisição:', detail, life: 4000});

  useEffect(() => {
    if (mode === 'code' && challenge && expiresIn === 0) {
      emailForm.setValue('email', challenge.email);
      codeForm.reset();
      setChallenge(null);
      setMode('email');
      showToast({severity: 'warn', summary: 'Código expirado', detail: 'Solicite um novo código.', life: 4000});
    }
  }, [expiresIn, mode, challenge, emailForm, codeForm, showToast]);

  const passwordLogin = async (data: {email: string; password: string}) => {
    try {
      setLoading(true);
      await signIn({...data, type: 'boss'});
    } catch (error: any) {
      errorToast(error.message === 'The user credentials were incorrect.' ? 'E-mail/Senha inválidos.' : error.message);
    } finally { setLoading(false); }
  };

  const sendCode = async (email: string) => {
    try {
      setLoading(true);
      const {data} = await requestOtp(email);
      const timestamp = Date.now();
      setNow(timestamp);
      setChallenge({id: data.challenge_id, email, message: data.message,
        expiresAt: timestamp + data.expires_in * 1000, resendAt: timestamp + data.retry_after * 1000});
      codeForm.reset();
      setMode('code');
    } catch (error: any) {
      errorToast(error.response?.data?.message || 'Não foi possível enviar o código. Tente novamente.');
      const retryAfter = Number(error.response?.data?.retry_after || 0);
      if (challenge && retryAfter) setChallenge({...challenge, resendAt: Date.now() + retryAfter * 1000});
    } finally { setLoading(false); }
  };

  const verifyCode = async ({code}: {code: string}) => {
    if (!challenge || expiresIn === 0) return errorToast('Código inválido ou expirado. Solicite um novo código.');
    try {
      setLoading(true);
      await signInWithOtp({challengeId: challenge.id, code});
      setChallenge(null);
      codeForm.reset();
    } catch {
      errorToast('Código inválido ou expirado. Solicite um novo código.');
      codeForm.setFocus('code');
    } finally { setLoading(false); }
  };

  const cancelOtp = () => {
    setChallenge(null); emailForm.reset(); codeForm.reset(); setMode('password');
  };

  return <div>
    <Head><title>Página de Login - Plataforma de Serviços Formadores Credenciados</title></Head>
    <main><div className="min-h-screen flex">
      <div className="surface-section w-full md:w-6 p-6 md:p-8">
        <div className="mb-5">
          <Image src="/logo-customer.png" alt="logo-customer" width={200} height={80} className="mb-3 fadein animation-duration-1000"/>
          <h3 className="text-3xl font-medium mb-3 fadein animation-duration-1000" style={{color: '#293988'}}>
            Bem vindo de volta, <b className="underline">Chefe do Programa!</b>
          </h3>
          <p className="font-light mr-2 fadein animation-duration-1000" style={{color: '#293988'}}>Plataforma de Serviços Formadores Credenciados</p>
        </div>

        {mode === 'password' && <form onSubmit={passwordForm.handleSubmit(passwordLogin)} className="fadein animation-duration-1000">
          <label htmlFor="email" className="block text-900 font-medium mb-2">E-mail:</label>
          <span className="p-input-icon-left mb-2" style={{display: 'flex'}}><i className="pi pi-envelope"/>
            <Controller name="email" control={passwordForm.control} render={({field, fieldState}) =>
              <InputText {...field} id="email" placeholder="Insira seu e-mail" type="email" autoFocus autoComplete="username"
                         className={classNames({'p-invalid': fieldState.invalid}, 'w-full')}/>}/>
          </span><FieldMessage name="email" error={passwordForm.formState.errors.email}/>
          <label htmlFor="password" className="block text-900 font-medium mt-3 mb-2">Senha:</label>
          <span className="p-input-icon-left mb-2" style={{width: '100%'}}>
            <Controller name="password" control={passwordForm.control} render={({field, fieldState}) =>
              <Password {...field} id="password" placeholder="Insira sua senha" feedback={false} toggleMask={false}
                        autoComplete="current-password" style={{width: '100%'}} className={classNames({'p-invalid': fieldState.invalid})}/>}/>
          </span><FieldMessage name="password" error={passwordForm.formState.errors.password}/>
          <div className="flex align-items-center justify-content-between mt-3 mb-4"><span/>
            <Link href="/auth/recovery-password" style={{color: '#29378b'}} className="font-medium no-underline ml-2 text-right cursor-pointer">Esqueceu sua senha ?</Link>
          </div>
          <Button type="submit" label="Entrar" loading={loading} disabled={loading} icon="pi pi-sign-in" className="w-full py-3"/>
          <Button type="button" label="Entrar com código por e-mail" outlined className="w-full py-3 mt-3"
                  onClick={() => {emailForm.setValue('email', passwordForm.getValues('email')); setMode('email');}}/>
        </form>}

        {mode === 'email' && <form onSubmit={emailForm.handleSubmit(({email}) => sendCode(email))} className="fadein animation-duration-1000">
          <div className="mb-4">
            <h4 className="text-lg font-medium mt-0 mb-2" style={{color: '#293988'}}>Entrar com código por e-mail</h4>
            <p className="text-600 font-light line-height-3 mt-0 mb-0">Enviaremos um código de seis dígitos para seu e-mail.</p>
          </div>
          <label htmlFor="otp-email" className="block text-900 font-medium mb-2">E-mail:</label>
          <span className="p-input-icon-left mb-2" style={{display: 'flex'}}><i className="pi pi-envelope"/>
            <Controller name="email" control={emailForm.control} render={({field, fieldState}) =>
              <InputText {...field} id="otp-email" placeholder="Insira seu e-mail" type="email" autoFocus autoComplete="email"
                         className={classNames({'p-invalid': fieldState.invalid}, 'w-full')}/>}/>
          </span><FieldMessage name="email" error={emailForm.formState.errors.email}/>
          <Button type="submit" label="Enviar código" loading={loading} disabled={loading} icon="pi pi-envelope" className="w-full py-3 mt-4"/>
          <Button type="button" label="Voltar para entrar com senha" text className="w-full mt-2" onClick={cancelOtp}/>
        </form>}

        {mode === 'code' && challenge && <form onSubmit={codeForm.handleSubmit(verifyCode)} className="fadein animation-duration-1000">
          <h4 className="text-xl mb-2">Digite o código</h4>
          <p className="mt-0 mb-2" role="status">{challenge.message}</p>
          <p className="mt-0 mb-4">Enviado para <b>{maskEmail(challenge.email)}</b>.</p>
          <label htmlFor="code" className="block text-900 font-medium mb-2">Código de acesso:</label>
          <Controller name="code" control={codeForm.control} render={({field, fieldState}) =>
            <InputText {...field} id="code" autoFocus inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000"
                       aria-describedby="code-help" onChange={event => field.onChange(event.target.value.replace(/\D/g, '').slice(0, 6))}
                       className={classNames({'p-invalid': fieldState.invalid}, 'w-full text-center text-2xl')}/>}/>
          <FieldMessage name="code" error={codeForm.formState.errors.code}/>
          <small id="code-help" className="block mt-2" aria-live="polite">O código expira em {Math.floor(expiresIn / 60)}:{String(expiresIn % 60).padStart(2, '0')}.</small>
          <Button type="submit" label="Validar código" loading={loading} disabled={loading || expiresIn === 0} icon="pi pi-sign-in" className="w-full py-3 mt-4"/>
          <Button type="button" label={resendIn ? `Reenviar código em ${resendIn}s` : 'Reenviar código'} outlined className="w-full py-3 mt-3"
                  disabled={loading || resendIn > 0} onClick={() => sendCode(challenge.email)}/>
          <Button type="button" label="Voltar e trocar o e-mail" text className="w-full mt-2" onClick={() => {
            const email = challenge.email; setChallenge(null); codeForm.reset(); emailForm.setValue('email', email); setMode('email');
          }}/>
        </form>}

        <div className="w-full text-center font-medium mt-4"><Link href="/auth/login-as-fellow" style={{color: '#29378b'}}
          className="w-full text-center font-medium underline cursor-pointer">Entrar como fellow</Link></div>
      </div>
      <div className="hidden md:block w-6 bg-no-repeat bg-cover fadein animation-duration-1000"
           style={{backgroundImage: "url('/login-manager-image.png')", backgroundPosition: 'center'}}/>
    </div></main>
  </div>;
};

export default LoginAsManager;
